import {
  createContext,
  useRef,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import { supabase } from "./supabase";
import { useAuth } from "./AuthContext";
import {
  getLocalizedDayName,
  getLocalizedAppointmentType,
} from "../i18n/scheduleLabels";
import {
  SCHEDULE_TYPE,
  CONSULTATION_MODE,
  formatClockTime,
  formatDate,
  getScheduleSpecificDates,
  isSlotInPast,
  getScheduleType,
  normalizeAppointmentTimes,
  normalizeScheduleTimes,
} from "./ScheduleUtils";
import {
  normalizePatientBookingHorizonDays,
  isAppointmentDateWithinHorizon,
} from "./bookingHorizon";
import customToast from "../components/common/Toaster";
import { isBookableDoctor } from "../utils/utils";
import tr from "../i18n/translate";

const DataContext = createContext(undefined);

export function DataProvider({ children }) {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [secretaries, setSecretaries] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [sharedSchedules, setSharedSchedules] = useState([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [clinicalHistories, setClinicalHistories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [doctorSecretaries, setDoctorSecretaries] = useState([]);
  const [professionalsAssociations, setProfessionalsAssociations] = useState([]);

  const profileCache = useRef(new Map());

  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Cargar datos cuando el usuario cambie
  useEffect(() => {
    if (user) {
      loadData();
    } else {
      setSchedules([]);
      setSecretaries([]);
      setProfiles([]);
      setDoctors([]);
      setAppointments([]);
      setClinicalHistories([]);
      setSharedSchedules([]);
      setDoctorSecretaries([]);
      setProfessionalsAssociations([]);
    }
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      // console.log("📊 Cargando datos para usuario:", user.id);
      await Promise.all([
        loadSchedules(),
        loadSecretaries(),
        loadDoctors(),
        loadClinicalHistories(),
        loadProfiles(),
        loadAppointments(),
        loadSharedSchedules(),
        loadDoctorSecretaries(),
        loadProfessionalsAssociations(),
      ]);
    } catch (error) {
      console.error("💥 Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadClinicalHistories = async () => {
    try {
      const { data, error } = await supabase
        .from("clinical_histories")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      if (isMounted.current) {
        setClinicalHistories(data || []);
      }
    } catch (error) {
      console.error("Error loading clinical histories:", error);
      if (isMounted.current) {
        setClinicalHistories([]);
      }
    }
  };

  const loadProfiles = async () => {
    try {
      const { data, error } = await supabase.from("profiles").select("*");

      if (error) throw error;
      setProfiles(data || []);
    } catch (error) {
      console.error("Error loading profiles:", error);
      setProfiles([]);
    }
  };

  const loadSecretaries = async () => {
    try {
      // console.log("📅 Cargando secretarias...");
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .in("role", ["institution", "secretary"])
        .order("last_name", { ascending: true });

      if (error) {
        console.error("❌ Error cargando secretarias:", error);
        throw error;
      }

      // console.log("✅ Secretarias cargadas:", data?.length || 0);
      setSecretaries(data || []);
    } catch (error) {
      console.error("💥 Error en loadSecretaries:", error);
      setSecretaries([]);
    }
  };

  const loadDoctors = async () => {
    try {
      // console.log("📅 Cargando secretarias...");
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .in("role", ["doctor"])
        .order("last_name", { ascending: true });

      if (error) {
        console.error("❌ Error cargando doctores:", error);
        throw error;
      }

      // console.log("✅ Secretarias cargadas:", data?.length || 0);
      setDoctors((data || []).filter(isBookableDoctor));
    } catch (error) {
      console.error("💥 Error en loadSecretaries:", error);
      setDoctors([]);
    }
  };

  const loadSchedules = async () => {
    try {
      // console.log("📅 Cargando schedules...");
      const { data, error } = await supabase
        .from("schedules")
        .select("*")
        .eq("is_active", true)
        .order("day_of_week", { ascending: true });

      if (error) {
        console.error("❌ Error cargando schedules:", error);
        throw error;
      }

      // console.log("✅ Schedules cargados:", data?.length || 0);
      setSchedules((data || []).map(normalizeScheduleTimes));
    } catch (error) {
      console.error("💥 Error en loadSchedules:", error);
      setSchedules([]);
    }
  };

  const loadAppointments = async () => {
    try {
      // console.log("📋 Cargando appointments...");
      const { data, error } = await supabase
        .from("appointments")
        .select("*")
        .order("date", { ascending: true });

      if (error) {
        console.error("❌ Error cargando appointments:", error);
        throw error;
      }

      // console.log("✅ Appointments cargados:", data?.length || 0);
      setAppointments((data || []).map(normalizeAppointmentTimes));
    } catch (error) {
      console.error("💥 Error en loadAppointments:", error);
      setAppointments([]);
    }
  };

  const loadSharedSchedules = async () => {
    try {
      // console.log("🤝 Cargando shared schedules...");
      const { data, error } = await supabase
        .from("shared_schedules")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("❌ Error cargando shared schedules:", error);
        throw error;
      }

      // console.log("✅ Shared schedules cargados:", data?.length || 0);
      setSharedSchedules(data || []);
    } catch (error) {
      console.error("💥 Error en loadSharedSchedules:", error);
      setSharedSchedules([]);
    }
  };

  const loadDoctorSecretaries = async () => {
    try {
      const { data, error } = await supabase
        .from("doctor_secretaries")
        .select("*")
        .is("deleted_at", null);

      if (error) {
        console.error("❌ Error cargando doctor_secretaries:", error);
        throw error;
      }

      setDoctorSecretaries(data || []);
    } catch (error) {
      console.error("💥 Error en loadDoctorSecretaries:", error);
      setDoctorSecretaries([]);
    }
  };

  const loadProfessionalsAssociations = async () => {
    try {
      const { data, error } = await supabase
        .from("professionals_associations")
        .select("*")
        .eq("status", "active")
        .is("deleted_at", null);

      if (error) {
        console.error("❌ Error cargando professionals_associations:", error);
        throw error;
      }

      setProfessionalsAssociations(data || []);
    } catch (error) {
      console.error("💥 Error en loadProfessionalsAssociations:", error);
      setProfessionalsAssociations([]);
    }
  };

  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    let month = "" + (d.getMonth() + 1);
    let day = "" + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join("-");
  };

  const getClinicalHistoryById = useCallback(
    (id) => {
      return clinicalHistories.find((h) => h.id === id);
    },
    [clinicalHistories]
  );

  // Memoizar getClinicalHistoryByAppointmentId
  const getClinicalHistoryByAppointmentId = useCallback(
    (appointmentId) => {
      return clinicalHistories.find((h) => h.appointment_id === appointmentId);
    },
    [clinicalHistories]
  );

  const addClinicalHistory = async (historyData) => {
    try {
      const { data, error } = await supabase
        .from("clinical_histories")
        .insert([
          {
            appointment_id: historyData.appointmentId,
            doctor_id: historyData.doctorId,
            patient_id: historyData.patientId,
            consultation_date: formatDate(historyData.consultationDate),
            notes: historyData.notes,
            image_urls: historyData.imageUrls || [],
          },
        ])
        .select();

      if (error) throw error;

      if (data && isMounted.current) {
        setClinicalHistories((prev) => [data[0], ...prev]);
        // Recargar appointments para actualizar has_clinical_history
        loadAppointments();
      }

      return { success: true, data: data[0] };
    } catch (error) {
      console.error("Error adding clinical history:", error);
      return { success: false, error: error.message };
    }
  };

  const updateClinicalHistory = async (historyId, historyData) => {
    try {
      const { data, error } = await supabase
        .from("clinical_histories")
        .update({
          consultation_date: formatDate(historyData.consultationDate),
          notes: historyData.notes,
          image_urls: historyData.imageUrls || [],
        })
        .eq("id", historyId)
        .select();

      if (error) throw error;

      if (data && isMounted.current) {
        setClinicalHistories((prev) =>
          prev.map((h) => (h.id === historyId ? data[0] : h))
        );
      }

      return { success: true, data: data[0] };
    } catch (error) {
      console.error("Error updating clinical history:", error);
      return { success: false, error: error.message };
    }
  };

  const getProfileById = useCallback(
    async (id) => {
      if (!id) return null;

      try {
        // Nivel 1: Cache en memoria
        if (profileCache.current.has(id)) {
          return profileCache.current.get(id);
        }

        // Nivel 2: Estado local
        const localProfile = profiles.find((p) => p.id === id);
        if (localProfile) {
          profileCache.current.set(id, localProfile);
          return localProfile;
        }

        console.log(
          `Consultando perfil con ID: ${id} directamente de la base de datos`
        );
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", id)
          .single();

        if (error) {
          console.error(`Error obteniendo perfil ${id}:`, error);
          return null;
        }

        // Actualizar el caché local si el componente sigue montado
        if (isMounted.current) {
          setProfiles((prev) => {
            // Verificar si ya existe y reemplazarlo, o agregarlo si no existe
            const exists = prev.some((p) => p.id === data.id);
            if (exists) {
              return prev.map((p) => (p.id === data.id ? data : p));
            } else {
              return [...prev, data];
            }
          });
        }

        return data;
      } catch (error) {
        console.error(`Error en getProfileById para ${id}:`, error);
        return null;
      }
    },
    [profiles]
  );

  // Funciones que requieren autenticación real
  const requiresAuth = async () => {
    if (!user) {
      console.warn("⚠️ Operación requiere un usuario autenticado.");
      return false;
    }
    return true;
  };

  const timesOverlap = (startA, endA, startB, endB) =>
    startA < endB && endA > startB;

  const checkScheduleOverlap = ({
    doctorId,
    scheduleType,
    dayOfWeek,
    specificDates = [],
    startTime,
    endTime,
  }) => {
    const active = schedules.filter(
      (s) => s.doctor_id === doctorId && s.is_active
    );

    for (const existing of active) {
      const existingType = getScheduleType(existing);

      if (scheduleType === SCHEDULE_TYPE.BLOCKED) {
        if (existingType !== SCHEDULE_TYPE.BLOCKED) continue;
        const overlapDates = specificDates.filter((d) =>
          getScheduleSpecificDates(existing).includes(d)
        );
        if (overlapDates.length > 0) {
          return { hasOverlap: true, conflictingSchedule: existing, overlapDates };
        }
        continue;
      }

      if (existingType === SCHEDULE_TYPE.BLOCKED) continue;

      const conflictsOnDate = (dateStr) => {
        const dow = new Date(`${dateStr}T12:00:00`).getDay();
        if (existingType === SCHEDULE_TYPE.HABITUAL) {
          return existing.day_of_week === dow;
        }
        if (existingType === SCHEDULE_TYPE.OCCASIONAL) {
          return getScheduleSpecificDates(existing).includes(dateStr);
        }
        return false;
      };

      if (scheduleType === SCHEDULE_TYPE.HABITUAL) {
        if (
          existingType === SCHEDULE_TYPE.HABITUAL &&
          existing.day_of_week === dayOfWeek &&
          timesOverlap(startTime, endTime, existing.start_time, existing.end_time)
        ) {
          return { hasOverlap: true, conflictingSchedule: existing };
        }
        if (existingType === SCHEDULE_TYPE.OCCASIONAL) {
          for (const dateStr of getScheduleSpecificDates(existing)) {
            const dow = new Date(`${dateStr}T12:00:00`).getDay();
            if (
              dow === dayOfWeek &&
              timesOverlap(
                startTime,
                endTime,
                existing.start_time,
                existing.end_time
              )
            ) {
              return {
                hasOverlap: true,
                conflictingSchedule: existing,
                conflictDate: dateStr,
              };
            }
          }
        }
        continue;
      }

      if (scheduleType === SCHEDULE_TYPE.OCCASIONAL) {
        for (const dateStr of specificDates) {
          if (
            conflictsOnDate(dateStr) &&
            timesOverlap(
              startTime,
              endTime,
              existing.start_time,
              existing.end_time
            )
          ) {
            return {
              hasOverlap: true,
              conflictingSchedule: existing,
              conflictDate: dateStr,
            };
          }
        }
      }
    }

    return { hasOverlap: false };
  };

  /**
   * Inserta una fila; si falla porque la columna consultation_mode todavía no existe
   * (migración 20260930_consultation_mode.sql sin aplicar), reintenta sin ella.
   */
  const insertWithConsultationModeFallback = async (table, row) => {
    const result = await supabase.from(table).insert([row]).select();
    const missingColumn =
      result.error &&
      String(result.error.message || "").includes("consultation_mode");
    if (!missingColumn) return result;
    console.warn(
      `⚠️ ${table}.consultation_mode no existe: aplicar migración 20260930_consultation_mode.sql`
    );
    const { consultation_mode, ...rowWithoutMode } = row;
    return supabase.from(table).insert([rowWithoutMode]).select();
  };

  const addSchedule = async (scheduleData) => {
    try {
      const hasAuth = await requiresAuth();
      if (!hasAuth) {
        console.warn("⚠️ addSchedule requiere login real");
        return { success: false, error: "Autenticación requerida" };
      }

      const scheduleType =
        scheduleData.scheduleType || SCHEDULE_TYPE.HABITUAL;
      const specificDates = scheduleData.specificDates || [];

      if (
        scheduleType === SCHEDULE_TYPE.OCCASIONAL ||
        scheduleType === SCHEDULE_TYPE.BLOCKED
      ) {
        if (specificDates.length === 0) {
          return {
            success: false,
            error: "Selecciona al menos una fecha",
          };
        }
      }

      if (scheduleType === SCHEDULE_TYPE.BLOCKED) {
        if (!scheduleData.blockReason?.trim()) {
          return {
            success: false,
            error: "Indica el motivo del bloqueo (ej. Feriado, Vacaciones)",
          };
        }
      }

      const overlapCheck = checkScheduleOverlap({
        doctorId: scheduleData.doctorId,
        scheduleType,
        dayOfWeek: scheduleData.dayOfWeek,
        specificDates,
        startTime: scheduleData.startTime,
        endTime: scheduleData.endTime,
      });

      if (overlapCheck.hasOverlap) {
        const conflictingSchedule = overlapCheck.conflictingSchedule;
        if (scheduleType === SCHEDULE_TYPE.BLOCKED) {
          return {
            success: false,
            error:
              "Ya existe un bloqueo que incluye alguna de las fechas seleccionadas.",
          };
        }
        const dayName = getLocalizedDayName(
          Number.parseInt(conflictingSchedule.day_of_week)
        );
        const typeName = getLocalizedAppointmentType(
          conflictingSchedule.appointment_type
        );
        const dateHint = overlapCheck.conflictDate
          ? ` (${overlapCheck.conflictDate})`
          : "";

        return {
          success: false,
          error: `Ya existe un horario${dateHint} el ${dayName} de ${formatClockTime(conflictingSchedule.start_time)} a ${formatClockTime(conflictingSchedule.end_time)} (${typeName}). No se puede superponer con el nuevo horario.`,
        };
      }

      const resolvedDayOfWeek =
        scheduleData.dayOfWeek ??
        (specificDates[0]
          ? new Date(`${specificDates[0]}T12:00:00`).getDay()
          : 0);

      const blockedTimes =
        scheduleType === SCHEDULE_TYPE.BLOCKED
          ? { start: "00:00", end: "23:59" }
          : {
              start: scheduleData.startTime,
              end: scheduleData.endTime,
            };

      const insertRow = {
        doctor_id: scheduleData.doctorId,
        day_of_week: resolvedDayOfWeek,
        start_time: formatClockTime(blockedTimes.start),
        end_time: formatClockTime(blockedTimes.end),
        appointment_type:
          scheduleType === SCHEDULE_TYPE.BLOCKED
            ? "private"
            : scheduleData.appointmentType,
        slot_duration:
          scheduleType === SCHEDULE_TYPE.BLOCKED
            ? 30
            : scheduleData.slotDuration,
        is_active: scheduleData.isActive,
        schedule_type: scheduleType,
        specific_dates:
          scheduleType === SCHEDULE_TYPE.HABITUAL ? null : specificDates,
        block_reason:
          scheduleType === SCHEDULE_TYPE.BLOCKED
            ? scheduleData.blockReason.trim()
            : null,
        consultation_mode:
          scheduleType === SCHEDULE_TYPE.BLOCKED
            ? CONSULTATION_MODE.IN_PERSON
            : scheduleData.consultationMode || CONSULTATION_MODE.IN_PERSON,
      };

      const { data, error } = await insertWithConsultationModeFallback(
        "schedules",
        insertRow
      );

      if (error) throw error;
      if (data) {
        setSchedules((prev) => [...prev, ...data.map(normalizeScheduleTimes)]);
      }
      return { success: true };
    } catch (error) {
      console.error("💥 Error adding schedule:", error);
      return { success: false, error: "Error al agregar el horario" };
    }
  };

  const updateSchedule = async (id, updates) => {
    try {
      const hasAuth = await requiresAuth();
      if (!hasAuth) return false;

      const { data, error } = await supabase
        .from("schedules")
        .update(updates)
        .eq("id", id)
        .select();

      if (error) throw error;
      if (data) {
        setSchedules((prev) =>
          prev.map((s) => (s.id === id ? normalizeScheduleTimes(data[0]) : s))
        );
      }
      return true;
    } catch (error) {
      console.error("💥 Error updating schedule:", error);
      return false;
    }
  };

  const deleteSchedule = async (id) => {
    try {
      const hasAuth = await requiresAuth();
      if (!hasAuth) return false;

      const { error } = await supabase.from("schedules").delete().eq("id", id);

      if (error) throw error;
      setSchedules((prev) => prev.filter((s) => s.id !== id));
      return true;
    } catch (error) {
      console.error("💥 Error deleting schedule:", error);
      return false;
    }
  };

  const addAppointment = async (appointmentData) => {
    /** userNotified: ya se mostró customToast en esta función — el llamador no debe duplicar mensaje genérico */
    try {
      const hasAuth = await requiresAuth();
      if (!hasAuth) {
        console.warn("⚠️ addAppointment requiere login real");
        return { ok: false, appointment: null, userNotified: false };
      }

      const dateStr = formatDate(appointmentData.date);

      const { data: doctorProfile, error: doctorError } = await supabase
        .from("profiles")
        .select("id, role, is_professional_enabled, patient_booking_horizon_days")
        .eq("id", appointmentData.doctorId)
        .maybeSingle();

      if (doctorError) throw doctorError;

      if (!isBookableDoctor(doctorProfile)) {
        customToast(
          tr(
            "app.appointments.doctorNotAvailable",
            "Este profesional no está disponible para recibir turnos."
          ),
          "danger"
        );
        return { ok: false, appointment: null, userNotified: true };
      }

      const bookingHorizonDays = normalizePatientBookingHorizonDays(
        doctorProfile?.patient_booking_horizon_days
      );

      // Horario que ya empezó (turno en el mismo día): se asigna desde el siguiente slot.
      if (isSlotInPast(dateStr, appointmentData.startTime)) {
        customToast(
          tr(
            "app.appointments.slotInPast",
            "Ese horario ya pasó. Elegí uno a partir de la hora actual."
          ),
          "danger"
        );
        return { ok: false, appointment: null, userNotified: true };
      }

      // El horizonte de reserva (desde mañana) es solo para pacientes; profesional,
      // secretaria e institución asignan sin límite, incluido el mismo día.
      if (
        !appointmentData.isOverbook &&
        !appointmentData.secretaryId &&
        !appointmentData.assignedByStaff &&
        !isAppointmentDateWithinHorizon(appointmentData.date, bookingHorizonDays)
      ) {
        customToast(
          tr(
            "app.appointments.dateOutOfRange",
            "Fecha fuera del rango permitido"
          ),
          "danger"
        );
        return { ok: false, appointment: null, userNotified: true };
      }

      if (!appointmentData.isOverbook) {
        // Verificar si ya existe un turno para el mismo profesional, fecha y horario
        const { data: slotConflict, error: slotError } = await supabase
          .from("appointments")
          .select("id, patient_name")
          .eq("doctor_id", appointmentData.doctorId)
          .eq("date", dateStr)
          .eq("start_time", formatClockTime(appointmentData.startTime))
          .in("status", ["booked", "confirmed"])
          .limit(1);

        if (slotError) throw slotError;

        if (slotConflict && slotConflict.length > 0) {
          customToast(
            tr(
              "app.appointments.slotTaken",
              "Este horario ya está ocupado. Por favor selecciona otro horario disponible."
            ),
            "danger"
          );
          return { ok: false, appointment: null, userNotified: true };
        }

        // Si hay patient_id, verificar que el mismo paciente no tenga otro turno ese día a esa hora
        if (appointmentData.patientId) {
          const { data: patientConflict, error: patientError } = await supabase
            .from("appointments")
            .select("id")
            .eq("patient_id", appointmentData.patientId)
            .eq("date", dateStr)
            .eq("start_time", formatClockTime(appointmentData.startTime))
            .in("status", ["booked", "confirmed"])
            .limit(1);

          if (patientError) throw patientError;

          if (patientConflict && patientConflict.length > 0) {
            customToast(
              tr(
                "app.appointments.patientSlotTaken",
                "Ya tenés un turno reservado en este horario."
              ),
              "danger"
            );
            return { ok: false, appointment: null, userNotified: true };
          }
        }
      }

      const { data, error } = await insertWithConsultationModeFallback(
        "appointments",
        {
          doctor_id: appointmentData.doctorId,
          patient_id: appointmentData.patientId,
          secretary_id: appointmentData.secretaryId,
          date: dateStr,
          start_time: formatClockTime(appointmentData.startTime),
          end_time: formatClockTime(appointmentData.endTime),
          appointment_type: appointmentData.appointmentType,
          status: appointmentData.status,
          price: appointmentData.price,
          patient_name: appointmentData.patientName,
          patient_phone: appointmentData.patientPhone,
          notes: appointmentData.notes,
          consultation_mode: appointmentData.consultationMode || null,
        }
      );

      if (error) throw error;
      if (data) {
        setAppointments((prev) => [...prev, ...data.map(normalizeAppointmentTimes)]);
      }
      return { ok: true, appointment: data?.[0] ?? null, userNotified: false };
    } catch (error) {
      console.error("💥 Error adding appointment:", error);
      return { ok: false, appointment: null, userNotified: false };
    }
  };

  // Crear una versión síncrona de getProfileById para búsquedas en caché
  const getProfileFromCache = useCallback(
    (id) => {
      return profiles.find((p) => p.id === id);
    },
    [profiles]
  );

  const updateAppointment = async (id, updates) => {
    try {
      const { data, error } = await supabase
        .from("appointments")
        .update(updates)
        .eq("id", id)
        .select();

      if (error) throw error;
      if (data) {
        // console.log("datasetAppointments2", data);
        setAppointments((prev) =>
          prev.map((a) => (a.id === id ? normalizeAppointmentTimes(data[0]) : a))
        );
      }
      return true;
    } catch (error) {
      console.error("Error updating appointment:", error);
      return false;
    }
  };

  const shareSchedule = async (scheduleId, secretaryId, doctorId) => {
    try {
      const hasAuth = await requiresAuth();
      if (!hasAuth) return false;

      const { data, error } = await supabase
        .from("shared_schedules")
        .insert([
          {
            schedule_id: scheduleId,
            secretary_id: secretaryId,
            doctor_id: doctorId,
          },
        ])
        .select();

      if (error) throw error;
      if (data) {
        setSharedSchedules((prev) => [...prev, ...data]);
      }
      return true;
    } catch (error) {
      console.error("💥 Error sharing schedule:", error);
      return false;
    }
  };

  const removeSharedSchedule = async (scheduleId) => {
    try {
      const hasAuth = await requiresAuth();
      if (!hasAuth) return false;

      const { error } = await supabase
        .from("shared_schedules")
        .delete()
        .eq("schedule_id", scheduleId);

      if (error) throw error;
      setSharedSchedules((prev) =>
        prev.filter((s) => s.schedule_id !== scheduleId)
      );
      return true;
    } catch (error) {
      console.error("💥 Error removing shared schedule:", error);
      return false;
    }
  };

  const getSchedulesByDoctor = (doctorId) => {
    const doctor = profiles.find((p) => p.id === doctorId);
    if (!isBookableDoctor(doctor)) return [];
    return schedules.filter((s) => s.doctor_id === doctorId && s.is_active);
  };

  const getAppointmentsByDoctor = (doctorId) => {
    return appointments.filter((a) => a.doctor_id === doctorId);
  };

  const getSharedSchedulesBySecretary = (secretaryId) => {
    return sharedSchedules.filter((s) => s.secretary_id === secretaryId);
  };

  const getSecretaries = () => {
    return secretaries;
  };

  const getDoctorsForSecretary = useCallback(
    (secretaryId) => {
      if (!secretaryId) return [];

      const directDoctorIds = doctorSecretaries
        .filter((relation) => relation.secretary_id === secretaryId)
        .map((relation) => relation.doctor_id);

      const scheduleDoctorIds = sharedSchedules
        .filter((shared) => shared.secretary_id === secretaryId)
        .map((shared) => {
          const schedule = schedules.find(
            (scheduleItem) => scheduleItem.id === shared.schedule_id
          );
          return schedule?.doctor_id;
        })
        .filter(Boolean);

      const uniqueDoctorIds = new Set([
        ...directDoctorIds,
        ...scheduleDoctorIds,
      ]);

      if (uniqueDoctorIds.size === 0) return [];

      return profiles.filter(
        (profile) =>
          uniqueDoctorIds.has(profile.id) && isBookableDoctor(profile)
      );
    },
    [doctorSecretaries, sharedSchedules, schedules, profiles]
  );

  return (
    <DataContext.Provider
      value={{
        schedules,
        clinicalHistories,
        appointments,
        doctors,
        sharedSchedules,
        loading,
        profiles,
        secretaries,
        doctorSecretaries,
        professionalsAssociations,
        addSchedule,
        updateSchedule,
        deleteSchedule,
        addAppointment,
        addClinicalHistory,
        updateClinicalHistory,
        updateAppointment,
        shareSchedule,
        removeSharedSchedule,
        getClinicalHistoryById,
        getProfileById,
        getSchedulesByDoctor,
        getProfileFromCache,
        getAppointmentsByDoctor,
        getClinicalHistoryByAppointmentId,
        getSharedSchedulesBySecretary,
        getSecretaries,
        getDoctorsForSecretary,
        loadData,
      }}
    >
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
}
